/**
 * The sign-in panel — a port of `PT Login Panel.dc.html`.
 *
 * The design offers Discord plus a passwordless email link. The brief asks for
 * Discord *in addition to* classic credentials, so the second half of the panel
 * is an email-and-password form wearing the design's clothes: same card, same
 * divider, same ghost button. Everything else — the four phases, the permission
 * box, the account confirmation card — follows the design as drawn.
 */

import { h } from '../vdom.js';
import { C, MONO, SANS, css, kicker, input, tap, termsCheck, legalLinks } from './common.js';

export function renderAuth(ctx) {
  const { state, t, mobile } = ctx;
  const s = t.shell;
  const phase = state.authPhase || 'idle';

  return h('div', {
    key: 'auth',
    style: css(mobile
      ? {
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          background: C.bg, color: C.text, fontFamily: SANS
        }
      : {
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'flex-start', gap: '20px',
          padding: '32px', background: `radial-gradient(120% 80% at 50% 0%, ${C.card} 0%, ${C.bg} 62%)`,
          color: C.text, fontFamily: SANS
        })
  },
    h('div', {
      style: css(mobile
        ? {
            flex: 1, display: 'flex', flexDirection: 'column', gap: '22px',
            padding: 'calc(22px + env(safe-area-inset-top, 0px)) 20px 26px', background: C.bg
          }
        : {
            width: '100%', maxWidth: '432px', display: 'flex', flexDirection: 'column',
            gap: '22px', padding: '28px', background: C.card,
            border: `1px solid ${C.line}`, borderRadius: '18px',
            boxShadow: '0 26px 60px rgba(0,0,0,.5)'
          })
    },
      brandRow(ctx),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '9px' }) },
        h('div', {
          style: css({
            fontSize: mobile ? '24px' : '26px', fontWeight: 800,
            letterSpacing: mobile ? '-0.8px' : '-0.9px', lineHeight: 1.1
          })
        }, phase === 'reset' ? s.resetHeading : (state.authMode === 'signUp' ? s.headingSignUp : s.heading)),
        h('div', {
          style: css({ fontSize: '13.5px', lineHeight: 1.55, color: C.muted })
        }, phase === 'reset'
          ? s.resetSub
          : (state.authMode === 'signUp' ? s.subSignUp : s.subPassword))
      ),

      /*
       * One phase at a time, swapped by a click — keyed for the same reason
       * the tabs are.
       */
      h('div', { key: 'phase-' + phase },
        phase === 'idle' && idlePhase(ctx),
        phase === 'reset' && resetPhase(ctx),
        phase === 'connecting' && connectingPhase(ctx),
        phase === 'done' && donePhase(ctx),
        phase === 'error' && errorPhase(ctx)
      )
    ),

    h('div', {
      style: css(mobile
        ? { flex: '0 0 auto', display: 'flex', justifyContent: 'center', padding: '0 20px calc(12px + env(safe-area-inset-bottom, 0px))' }
        : { display: 'flex', justifyContent: 'center' })
    },
      h('div', {
        style: css({ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '1px', color: C.ghost })
      }, s.footer)
    )
  );
}

function brandRow({ state, t, actions }) {
  return h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '11px' }) },
    h('img', {
      src: 'logo-mark.png', alt: '',
      style: css({ width: '30px', height: '30px', objectFit: 'contain', flex: '0 0 30px' })
    }),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', minWidth: 0 }) },
      h('div', {
        style: css({
          fontSize: '15px', fontWeight: 800, fontStyle: 'italic',
          letterSpacing: '-0.5px', lineHeight: 1
        })
      }, 'PORTFOLIO TRACKER'),
      h('div', {
        style: css({
          fontFamily: MONO, fontSize: '8.5px', letterSpacing: '1.5px',
          color: C.accent, marginTop: '4px'
        })
      }, t.brandKicker)
    ),
    h('div', { style: css({ flex: 1 }) }),
    h('div', {
      ...tap(() => actions.setLanguage(state.lang === 'PL' ? 'EN' : 'PL'), t.shell.ariaLanguage),
      style: css({
        flex: '0 0 auto', minWidth: '44px', height: '32px', display: 'flex',
        alignItems: 'center', justifyContent: 'center', border: `1px solid ${C.stroke}`,
        borderRadius: '16px', background: C.panel, fontFamily: MONO, fontSize: '10.5px',
        fontWeight: 700, letterSpacing: '1px', color: C.accent, cursor: 'pointer'
      })
    }, state.lang)
  );
}

// ------------------------------------------------------------------ idle ----

/* Said here, in words: the server's refusal only reached the form as "check the figures". */
const looksLikeEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

function idlePhase(ctx) {
  const { state, t, inputs, actions, mobile } = ctx;
  const s = t.shell;
  const isSignUp = state.authMode === 'signUp';

  const isForgot = state.authMode === 'forgot';

  const submit = () => {
    const email = inputs.value('authEmail').trim();
    const password = inputs.value('authPassword');

    if (!email) return actions.showError(s.emailNeeded);
    if (!looksLikeEmail(email)) return actions.showError(s.emailInvalid);
    if (isForgot) return actions.requestReset(email);
    if (isSignUp) {
      if (password.length < 8) return actions.showError(s.passwordShort);
      if (password !== inputs.value('authPasswordAgain')) {
        return actions.showError(s.passwordsDiffer);
      }
      if (!state.termsTicked) return actions.showError(s.termsNeeded);
      return actions.signUp({ email, password });
    }
    return actions.signIn({ email, password });
  };

  /*
   * Email and the demo first, Discord one line further down, in both
   * languages. Discord led the screen while the app's users came from its
   * Discord server; most people arriving now (Product Hunt, search) are not
   * on it, and a screen that opens on "Connect your Discord account" reads
   * as if that were required.
   */
  const discordLast = state.discordEnabled && !isForgot;

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '16px' }) },

    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '9px' }) },
      field(inputs, 'authEmail', s.email, 'email', mobile, submit),
      !isForgot && field(inputs, 'authPassword', s.password, 'password', mobile, submit),
      isSignUp && field(inputs, 'authPasswordAgain', s.passwordAgain, 'password', mobile, submit),
      isSignUp && termsCheck({
        checked: state.termsTicked,
        onToggle: () => actions.setTermsTicked(!state.termsTicked),
        t, lang: state.lang, base: state.siteBase
      }),

      isForgot && h('div', {
        key: 'forgot-note',
        style: css({ fontSize: '11.5px', lineHeight: 1.5, color: C.faint, padding: '2px 2px 4px' })
      }, s.forgotNote),

      primaryButton(
        isForgot ? s.forgotSend : (isSignUp ? s.signUp : s.signIn),
        submit, mobile, state.busy
      ),

      h('div', {
        ...tap(() => actions.setAuthMode(isSignUp || isForgot ? 'signIn' : 'signUp')),
        style: css({
          textAlign: 'center', fontSize: '11.5px', color: C.muted,
          cursor: 'pointer', padding: '4px'
        })
      }, isForgot ? s.forgotBack : (isSignUp ? s.haveAccount : s.noAccount)),

      // Offered only where it makes sense: there is nothing to reset while
      // signing up, and the forgot screen is already here.
      !isSignUp && !isForgot && h('div', {
        key: 'forgot-link',
        ...tap(() => actions.setAuthMode('forgot')),
        style: css({
          textAlign: 'center', fontSize: '11.5px', color: C.faint,
          cursor: 'pointer', padding: '2px'
        })
      }, s.forgotLink)
    ),

    // Shown only after sign-in was refused for an unconfirmed address: at that
    // point there is no session, so this is the only route back in.
    state.unverifiedEmail && h('div', {
      key: 'unverified',
      style: css({
        display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px 15px',
        border: `1px solid ${C.stroke}`, borderRadius: '12px', background: C.panel
      })
    },
      h('div', {
        style: css({ fontSize: '12.5px', fontWeight: 600 })
      }, s.verifyBlocked),
      ghostButton(s.verifyResend, actions.resendVerification, mobile)
    ),

    ghostButton(s.tryDemo, actions.signInAsGuest, mobile),
    discordLast && divider(s.or),
    discordLast && discordButton(ctx, { quiet: true }),
    discordLast && h('div', {
      key: 'discord-note',
      style: css({ fontSize: '11px', lineHeight: 1.5, color: C.faint, textAlign: 'center', marginTop: '-8px' })
    }, s.discordNote),
    legalLinks({ t, lang: state.lang, base: state.siteBase })
  );
}

/**
 * Choosing the new password.
 *
 * Only reachable by following a link sent to the address on file, so there is
 * nothing left to prove here -- the one thing this screen owes the user is
 * refusing a password it knows the server will, before spending the link on it.
 */
function resetPhase({ state, t, inputs, actions, mobile }) {
  const s = t.shell;

  const submit = () => {
    const password = inputs.value('resetPassword');
    if (password.length < 8) return actions.showError(s.passwordShort);
    if (password !== inputs.value('resetPasswordAgain')) {
      return actions.showError(s.passwordsDiffer);
    }
    actions.submitReset(password);
  };

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '16px' }) },
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '9px' }) },
      field(inputs, 'resetPassword', s.newPassword, 'password', mobile, submit),
      field(inputs, 'resetPasswordAgain', s.passwordAgain, 'password', mobile, submit),
      primaryButton(s.resetSubmit, submit, mobile, state.busy)
    ),
    ghostButton(s.cancel, actions.cancelReset, mobile)
  );
}

/* `quiet`: the outlined second option under the email form, not the filled first one. */
function discordButton({ t, actions, mobile, state }, { quiet = false } = {}) {
  return h('div', {
    ...tap(() => actions.startDiscordSignIn(), t.shell.ctaDiscord),
    class: 'pt-button',
    style: css({
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
      height: mobile ? '52px' : '48px', borderRadius: '14px',
      background: quiet ? 'transparent' : C.accent, color: quiet ? C.text : C.bg, cursor: 'pointer',
      border: quiet ? `1px solid ${C.stroke}` : 'none',
      opacity: state.busy ? 0.6 : 1
    })
  },
    h('div', {
      style: css({
        width: '22px', height: '22px', flex: '0 0 22px', borderRadius: '7px',
        background: quiet ? C.accent : C.bg, color: quiet ? C.bg : C.accent, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontFamily: MONO, fontSize: '12px', fontWeight: 700
      })
    }, 'D'),
    h('div', {
      style: css({ fontFamily: MONO, fontSize: '12.5px', fontWeight: 700, letterSpacing: '.8px' })
    }, t.shell.ctaDiscord)
  );
}

const divider = label => h('div', {
  style: css({ display: 'flex', alignItems: 'center', gap: '12px' })
},
  h('div', { style: css({ flex: 1, height: '1px', background: C.line }) }),
  h('div', {
    style: css({ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '1.2px', color: C.ghost })
  }, label),
  h('div', { style: css({ flex: 1, height: '1px', background: C.line }) })
);

// ------------------------------------------------------------ connecting ----

function connectingPhase({ t, actions, mobile }) {
  const s = t.shell;

  return h('div', {
    class: 'pt-fade',
    style: css({ display: 'flex', flexDirection: 'column', gap: '18px' })
  },
    h('div', {
      style: css({
        display: 'flex', flexDirection: 'column', gap: '14px', padding: '20px 18px',
        border: `1px solid ${C.stroke}`, borderRadius: '14px', background: C.panel,
        alignItems: 'center', textAlign: 'center'
      })
    },
      h('div', {
        class: 'pt-spin',
        style: css({
          width: '34px', height: '34px', borderRadius: '50%',
          border: `3px solid ${C.line}`, borderTopColor: C.accent
        })
      }),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '5px' }) },
        h('div', { style: css({ fontSize: '14px', fontWeight: 700 }) }, s.connectingTitle),
        h('div', { style: css({ fontSize: '12px', lineHeight: 1.5, color: C.muted }) }, s.connectingBody)
      ),
      h('div', {
        style: css({ width: '100%', height: '4px', borderRadius: '2px', background: C.line, overflow: 'hidden' })
      },
        h('div', {
          class: 'pt-bar',
          style: css({ width: '32%', height: '100%', borderRadius: '2px', background: C.accent })
        })
      )
    ),
    ...s.steps.map((label, index) => h('div', {
      key: label,
      style: css({ display: 'flex', alignItems: 'center', gap: '11px' })
    },
      h('div', {
        style: css({
          width: '18px', height: '18px', flex: '0 0 18px', borderRadius: '6px',
          border: `1.5px solid ${index === 0 ? C.accent : C.strokeHi}`,
          background: index === 0 ? C.accent : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: MONO, fontSize: '10px', color: C.bg, fontWeight: 700
        })
      }, index === 0 ? '✓' : ''),
      h('div', {
        style: css({ fontSize: '12.5px', color: index === 0 ? C.textDim : C.faint })
      }, label)
    )),
    ghostButton(s.cancel, actions.resetAuth, mobile)
  );
}

// ------------------------------------------------------------------ done ----

function donePhase({ state, t, actions, mobile }) {
  const s = t.shell;
  const account = state.pendingAccount || state.user || {};
  const name = account.displayName || account.email || '—';
  const initials = String(name).trim().split(/\s+/).slice(0, 2)
    .map(part => part[0]).join('').toUpperCase() || '·';

  return h('div', {
    class: 'pt-fade',
    style: css({ display: 'flex', flexDirection: 'column', gap: '16px' })
  },
    h('div', {
      style: css({
        display: 'flex', alignItems: 'center', gap: '13px', padding: '15px',
        border: `1px solid ${C.accentDeep}`, borderRadius: '14px', background: C.panel
      })
    },
      account.avatarUrl
        ? h('img', {
            src: account.avatarUrl, alt: '',
            style: css({ width: '44px', height: '44px', flex: '0 0 44px', borderRadius: '14px', objectFit: 'cover' })
          })
        : h('div', {
            style: css({
              width: '44px', height: '44px', flex: '0 0 44px', borderRadius: '14px',
              background: C.line, border: `1px solid ${C.stroke}`, display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontFamily: MONO,
              fontSize: '15px', fontWeight: 700, color: C.accent
            })
          }, initials),
      h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }) },
        h('div', {
          style: css({
            fontSize: '13.5px', fontWeight: 700,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          })
        }, name),
        h('div', {
          style: css({
            fontFamily: MONO, fontSize: '11px', color: C.muted,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          })
        }, [account.discordHandle, account.email].filter(Boolean).join(' · ') || '—')
      ),
      h('div', {
        style: css({
          flex: '0 0 auto', fontFamily: MONO, fontSize: '9px', letterSpacing: '1.1px',
          color: C.accent, border: `1px solid ${C.accentDeep}`, borderRadius: '5px', padding: '3px 7px'
        })
      }, s.verified)
    ),

    h('div', {
      style: css({
        display: 'flex', flexDirection: 'column', gap: '8px', padding: '13px 15px',
        border: `1px solid ${C.line}`, borderRadius: '12px', background: C.panel
      })
    },
      infoRow(s.session, deviceLabel(state)),
      infoRow(s.tokenExpiry, '30 ' + (state.lang === 'PL' ? 'dni' : 'days'))
    ),

    primaryButton(s.enter, actions.enterLedger, mobile, state.busy),
    h('div', {
      ...tap(actions.signOut),
      class: 'pt-danger-hover',
      style: css({
        textAlign: 'center', fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.8px',
        color: C.faint, cursor: 'pointer', padding: '6px'
      })
    }, s.switchAccount)
  );
}

function deviceLabel(state) {
  if (state.platform === 'ios') return 'iOS';
  if (state.platform === 'android') return 'Android';
  return 'Web';
}

const infoRow = (label, value) => h('div', {
  style: css({
    display: 'flex', justifyContent: 'space-between',
    fontFamily: MONO, fontSize: '11px', color: C.faint
  })
}, h('span', null, label), h('span', { style: css({ color: C.text }) }, value));

// ----------------------------------------------------------------- error ----

function errorPhase({ state, t, actions, mobile }) {
  const s = t.shell;

  return h('div', {
    class: 'pt-fade',
    style: css({ display: 'flex', flexDirection: 'column', gap: '16px' })
  },
    h('div', {
      style: css({
        display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '15px',
        border: '1px solid #5A211C', borderRadius: '14px', background: '#140A0A'
      })
    },
      h('div', {
        style: css({
          width: '7px', height: '7px', borderRadius: '2px', flex: '0 0 7px',
          marginTop: '5px', background: C.danger
        })
      }),
      h('div', { style: css({ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }) },
        h('div', { style: css({ fontSize: '13px', fontWeight: 700, color: C.danger }) }, s.authErrorTitle),
        h('div', { style: css({ fontSize: '12px', lineHeight: 1.5, color: '#C6A9A6' }) }, s.authErrorBody),
        /*
         * Said in the reader's language, with the server's own words underneath.
         *
         * Both, because they answer different questions. Somebody mistyping a
         * password needs a sentence they can read; a misconfigured deployment
         * fails here too, and "the server would not accept that" alone sends
         * whoever is fixing it hunting through logs for something the response
         * already said.
         */
        state.error && state.error !== 'offline' && h('div', {
          style: css({
            fontSize: '11.5px', lineHeight: 1.45, color: C.textDim, marginTop: '2px'
          })
        }, state.error.startsWith('api:') ? apiErrorText(state.error, t) : state.error),
        state.error && state.errorDetail && h('div', {
          style: css({
            fontFamily: MONO, fontSize: '10px', lineHeight: 1.4, color: C.faint, marginTop: '2px'
          })
        }, state.errorDetail),
        h('div', {
          style: css({
            fontFamily: MONO, fontSize: '10px', letterSpacing: '.6px',
            color: C.muted, marginTop: '4px', wordBreak: 'break-all'
          })
        }, state.authErrorCode || 'unknown_error')
      )
    ),
    primaryButton(s.retry, () => actions.startDiscordSignIn(), mobile, state.busy),
    ghostButton(s.back, actions.resetAuth, mobile)
  );
}

// --------------------------------------------------------------- controls ---

const field = (inputs, name, placeholder, type, mobile, onEnter) =>
  input(inputs, name, placeholder, {
    type,
    radius: '14px',
    padding: '0 15px',
    style: { height: mobile ? '50px' : '46px', fontFamily: MONO, fontSize: '13px', width: '100%' },
    onEnter
  });

const primaryButton = (label, onClick, mobile, busy) => h('div', {
  ...tap(onClick),
  class: 'pt-button',
  style: css({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: mobile ? '52px' : '48px', borderRadius: '14px',
    background: C.accent, color: C.bg, cursor: 'pointer',
    fontFamily: MONO, fontSize: '12.5px', fontWeight: 700, letterSpacing: '.8px',
    opacity: busy ? 0.6 : 1
  })
}, label);

const ghostButton = (label, onClick, mobile) => h('div', {
  ...tap(onClick),
  class: 'pt-button',
  style: css({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: mobile ? '50px' : '46px', border: `1px solid ${C.stroke}`,
    borderRadius: '14px', background: 'transparent', color: C.muted,
    fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, letterSpacing: '.8px',
    cursor: 'pointer'
  })
}, label);

// ------------------------------------------------------- claim / toasts -----

/** Modal shown when a guest wants to keep the ledger they have built up. */
export function renderClaimDialog({ state, t, inputs, actions, mobile }) {
  const s = t.shell;

  const fresh = actions.claimFresh();

  const submit = () => {
    const email = inputs.value('claimEmail').trim();
    const password = inputs.value('claimPassword');
    if (!email) return actions.showError(s.emailNeeded);
    if (!looksLikeEmail(email)) return actions.showError(s.emailInvalid);
    if (password.length < 8) return actions.showError(s.passwordShort);
    if (password !== inputs.value('claimPasswordAgain')) {
      return actions.showError(s.passwordsDiffer);
    }
    if (!state.termsTicked) return actions.showError(s.termsNeeded);
    actions.claimGuestAccount({ email, password, fresh });
  };

  /*
   * What happens to the sample ledger, asked rather than assumed. It used to
   * be kept without a word, and a demo account became a real one still holding
   * somebody else's mortgage.
   */
  const choice = (on, title, note, pick) => h('div', {
    ...tap(pick, title),
    role: 'radio',
    'aria-checked': on ? 'true' : 'false',
    class: 'pt-button',
    style: css({
      display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer',
      padding: '10px 12px', borderRadius: '10px',
      border: `1px solid ${on ? C.accent : C.stroke}`,
      background: on ? C.accentDeep : 'transparent'
    })
  },
    h('div', {
      style: css({
        width: '14px', height: '14px', flex: '0 0 14px', marginTop: '2px', borderRadius: '50%',
        border: `2px solid ${on ? C.accent : C.stroke}`,
        background: on ? C.accent : 'transparent', boxShadow: on ? `inset 0 0 0 2px ${C.card}` : 'none'
      })
    }),
    h('div', { style: css({ flex: 1, minWidth: 0 }) },
      h('div', { style: css({ fontSize: '12.5px', fontWeight: 600, color: on ? C.accentHi : C.text }) }, title),
      h('div', { style: css({ fontSize: '11px', color: C.muted, lineHeight: 1.45, marginTop: '2px' }) }, note)
    )
  );

  return modal(
    h('div', { style: css({ fontSize: '15px', fontWeight: 700 }) }, s.claimTitle),
    h('div', { style: css({ fontSize: '12px', color: C.muted, lineHeight: 1.5 }) }, s.claimHint),
    field(inputs, 'claimEmail', s.email, 'email', mobile, submit),
    field(inputs, 'claimPassword', s.password, 'password', mobile, submit),
    field(inputs, 'claimPasswordAgain', s.passwordAgain, 'password', mobile, submit),
    h('div', { role: 'radiogroup', style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
      choice(fresh, s.claimFresh, s.claimFreshNote, () => actions.setClaimFresh(true)),
      choice(!fresh, s.claimKeep, s.claimKeepNote, () => actions.setClaimFresh(false))
    ),
    termsCheck({
      checked: state.termsTicked,
      onToggle: () => actions.setTermsTicked(!state.termsTicked),
      t, lang: state.lang, base: state.siteBase
    }),
    h('div', { style: css({ display: 'flex', gap: '8px' }) },
      h('div', { style: css({ flex: 1 }) }, ghostButton(s.cancel, actions.closeClaim, mobile)),
      h('div', { style: css({ flex: 1 }) }, primaryButton(s.signUp, submit, mobile, state.busy))
    )
  );
}

/**
 * The terms, for an account that has not accepted the version in force.
 *
 * Nothing behind it can be used until it is answered, and there is no way
 * around it but signing out: an account exists on accepted terms or not at all.
 */
export function renderTermsGate({ state, t, actions, mobile }) {
  const s = t.shell;
  /* The account already exists here, so the refusal says what to do, not what it takes to sign up. */
  const accept = () => (state.termsTicked ? actions.acceptTerms() : actions.showError(s.termsGateNeeded));

  return h('div', { key: 'terms-gate' }, modal(
    h('div', { style: css({ fontSize: '15px', fontWeight: 700 }) }, s.termsGateTitle),
    h('div', { style: css({ fontSize: '12px', color: C.muted, lineHeight: 1.5 }) }, s.termsGateBody),
    termsCheck({
      checked: state.termsTicked,
      onToggle: () => actions.setTermsTicked(!state.termsTicked),
      t, lang: state.lang, base: state.siteBase
    }),
    h('div', { style: css({ display: 'flex', gap: '8px' }) },
      h('div', { style: css({ flex: 1 }) }, ghostButton(s.signOut, () => actions.signOut(), mobile)),
      h('div', { style: css({ flex: 1 }) }, primaryButton(s.termsAccept, accept, mobile, state.busy))
    )
  ));
}

/**
 * Liquidity+ for three months, for the accounts that were here before the
 * plans. Said once, with the date it ends and what it includes -- and that
 * nothing is charged when it does, because "free for three months" is exactly
 * the sentence people expect a charge behind.
 */
export function renderGiftNotice({ state, t, actions, mobile }) {
  const s = t.shell;
  const until = state.user?.plan?.until;

  return h('div', { key: 'gift-notice' }, modal(
    h('div', {
      style: css({
        alignSelf: 'flex-start', padding: '4px 9px', borderRadius: '6px', background: C.accent,
        color: C.bg, fontFamily: MONO, fontSize: '9.5px', fontWeight: 700, letterSpacing: '1.2px'
      })
    }, 'LIQUIDITY+'),
    h('div', { style: css({ fontSize: '16px', fontWeight: 700, lineHeight: 1.3 }) }, s.giftTitle),
    h('div', { style: css({ fontSize: '12.5px', color: C.textDim, lineHeight: 1.55 }) },
      s.giftBody(until ? s.dateFull(until) : '')),
    h('ul', {
      style: css({
        margin: 0, paddingLeft: '18px', fontSize: '12px', color: C.muted, lineHeight: 1.7
      })
    }, ...s.giftIncludes.map(item => h('li', null, item))),
    h('div', { style: css({ fontSize: '11.5px', color: C.faint, lineHeight: 1.5 }) }, s.giftAfter),
    h('div', { style: css({ display: 'flex', gap: '8px' }) },
      h('div', { style: css({ flex: 1 }) }, ghostButton(s.giftSeePlan, () => actions.dismissGift({ showPlan: true }), mobile)),
      h('div', { style: css({ flex: 1 }) }, primaryButton(s.giftOk, () => actions.dismissGift(), mobile, false))
    )
  ));
}

const modal = (...children) => h('div', {
  key: 'modal',
  style: css({
    position: 'fixed', inset: 0, zIndex: 60, display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: '20px',
    background: 'rgba(4,8,12,.72)', overflowY: 'auto'
  })
},
  h('div', {
    role: 'dialog', 'aria-modal': 'true',
    style: css({
      width: '100%', maxWidth: '380px', background: C.card,
      border: `1px solid ${C.stroke}`, borderRadius: '16px', padding: '20px',
      display: 'flex', flexDirection: 'column', gap: '12px'
    })
  }, ...children)
);

/** A transient banner for API errors and confirmations. */
export function renderToast({ state, t, actions }) {
  const message = toastMessage(state, t, actions);
  if (!message) return null;

  return h('div', {
    key: 'toast',
    /* Spoken when it appears: an error at once, a confirmation when the reader is free. */
    role: message.tone === 'error' ? 'alert' : 'status',
    'aria-live': message.tone === 'error' ? 'assertive' : 'polite',
    style: css({
      position: 'fixed', left: '50%', transform: 'translateX(-50%)',
      bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))', zIndex: 80,
      maxWidth: 'min(420px, calc(100vw - 32px))',
      background: C.card, border: `1px solid ${message.tone === 'error' ? C.danger : C.accent}`,
      borderRadius: '12px', padding: '12px 14px', display: 'flex', gap: '12px',
      alignItems: 'center', boxShadow: '0 12px 30px rgba(0,0,0,.55)',
      fontFamily: SANS, color: C.text
    })
  },
    h('div', { style: css({ flex: 1, minWidth: 0, fontSize: '12.5px', lineHeight: 1.45 }) }, message.text),
    // An action, when the toast offers one — the undo window closes on its own,
    // so the button has to be right there rather than somewhere to navigate to.
    message.action && h('div', {
      ...tap(message.action.run),
      class: 'pt-button',
      style: css({
        flex: '0 0 auto', fontFamily: MONO, fontSize: '10.5px', fontWeight: 700,
        letterSpacing: '1px', color: C.bg, background: C.accent,
        borderRadius: '7px', padding: '7px 12px', cursor: 'pointer'
      })
    }, message.action.label),
    h('div', {
      ...tap(message.dismiss || actions.dismissError),
      style: css({
        flex: '0 0 auto', fontFamily: MONO, fontSize: '10px', letterSpacing: '1px',
        color: C.faint, cursor: 'pointer'
      })
    }, t.shell.dismiss)
  );
}

/*
 * Which form field the server was talking about, said the way the form says it.
 *
 * The field names are the API's own -- `units`, `yieldPct` and so on -- and
 * printing one at somebody is no better than printing the English sentence it
 * arrived in. Every one of these already has a placeholder written in both
 * languages, so the label the reader saw above the box is the label they get
 * back when it is refused.
 */
const API_FIELD = {
  units: 'phUnits', price: 'phPrice', cost: 'phAvgCost', yieldPct: 'phYield',
  amount: 'phAmount', label: 'phSource', ticker: 'phTicker', name: 'phName',
  balance: 'phBalance', original: 'phOriginal', rate: 'phApr', payment: 'phPayment',
  term: 'phTerm', fees: 'phFees', odo: 'phOdometer', gal: 'phCost', note: 'phNote'
};

function apiErrorText(key, t) {
  const s = t.shell;
  const [, code, field] = key.split(':');

  if (code === 'bad_request') {
    const named = field && API_FIELD[field] ? t[API_FIELD[field]] : null;
    return named ? s.apiRefusedField(named) : s.apiRefused;
  }

  return {
    not_found: s.apiNotFound,
    conflict: s.apiConflict,
    forbidden: s.apiForbidden,
    unauthorized: s.apiUnauthorized,
    wrong_password: s.apiWrongPassword,
    rate_limited: s.apiRateLimited,
    link_expired: s.resetExpired,
    terms_required: s.termsNeeded,
    quotes_disabled: s.apiQuotesOff,
    rates_disabled: s.apiQuotesOff,
    quotes_unavailable: s.apiQuotesDown,
    rates_unavailable: s.apiQuotesDown,
    plan_imports: s.apiPlanImports,
    plan_journal: s.apiPlanJournal,
    plan_backtests: s.apiPlanBacktests,
    plan_prop: s.apiPlanProp,
    plan_taxes: s.apiPlanTaxes,
    plan_quotes: s.apiPlanQuotes,
    ai_quota: s.apiAiQuota,
    ai_plan: s.ai_plan,
    ai_unavailable: s.ai_unavailable,
    ai_unconfigured: s.ai_unconfigured,
    billing_closed: s.apiBillingClosed,
    billing_active: s.apiBillingActive,
    billing_unavailable: s.apiBillingUnavailable,
    billing_guest: s.apiBillingGuest,
    billing_none: s.apiBillingNone,
    admin_stripe_live: s.apiAdminStripe,
    tax_rates_unavailable: s.apiTaxRates,
    ai_review_wait: s.apiReviewWait
  }[code] || s.apiRefused;
}

function toastMessage(state, t, actions) {
  const s = t.shell;

  /*
   * The undo offer outranks everything else: it expires, and the others do not.
   */
  if (state.undo) {
    return {
      tone: 'ok',
      text: state.undo.kind === 'payment'
        ? s.paymentLogged(state.undo.row.name)
        : s.undoDeleted(s.undoKinds[state.undo.kind] || state.undo.kind),
      action: { label: s.undo, run: actions.undoDelete },
      dismiss: actions.dismissUndo
    };
  }
  // Assistant failures arrive as codes so they can be said in the reader's language.
  if (typeof state.error === 'string' && state.error.startsWith('ai_') && s[state.error]) {
    return { tone: 'error', text: s[state.error] };
  }
  /*
   * So does everything else the server refuses. Its messages are English
   * sentences written for whoever reads the logs -- `"balance" must be between
   * 0 and 10000000000.` -- and they were being shown verbatim to somebody using
   * the app in Polish, internal field name and all.
   */
  if (typeof state.error === 'string' && state.error.startsWith('api:')) {
    return { tone: 'error', text: apiErrorText(state.error, t) };
  }
  if (state.error === 'verify:failed') return { tone: 'error', text: s.verifyFailed };
  if (state.error === 'reset:expired') return { tone: 'error', text: s.resetExpired };
  if (state.error === 'import:nothing') return { tone: 'error', text: s.importNothing };
  if (state.error === 'import:unreadable') return { tone: 'error', text: s.importUnreadable };
  if (state.error === 'import:empty') return { tone: 'error', text: s.importEmpty };
  if (state.error === 'tradeImport:unknown') return { tone: 'error', text: s.tiUnknown };
  if (state.error === 'tradeImport:empty') return { tone: 'error', text: s.tiEmpty };
  if (state.error === 'export:empty') return { tone: 'error', text: s.exportEmpty };
  if (state.error === 'push:blocked') return { tone: 'error', text: s.pushBlocked };
  if (state.error === 'push:unsupported') return { tone: 'error', text: s.pushNone };
  if (state.error === 'push:failed') return { tone: 'error', text: s.pushFailed };
  if (state.error) {
    return { tone: 'error', text: state.error === 'offline' ? s.offlineHint : state.error };
  }
  if (state.notice === 'prices:ok') return { tone: 'ok', text: s.pricesUpdated };
  if (state.notice?.startsWith('prices:partial:')) {
    const missed = state.notice.slice('prices:partial:'.length);
    /*
     * A euro ticker with no exchange cannot be priced -- euro is ten exchanges,
     * and the app will not guess one. Say how to fix it, not only that it failed.
     */
    const bare = missed.split(', ').filter(ticker => !ticker.includes('.')
      && (state.data?.holdings || []).some(row => row.ticker === ticker && row.currency === 'EUR'));
    return { tone: 'ok', text: s.pricesPartial(missed) + (bare.length ? ' ' + s.pricesEurHint(bare[0]) : '') };
  }
  /* Not an error and not a success -- a statement about work still in hand. */
  if (state.notice === 'billing:success') return { tone: 'ok', text: s.billingSuccess };
  if (state.notice === 'billing:pending') return { tone: 'ok', text: s.billingPending };
  if (state.notice === 'billing:cancel') return { tone: 'ok', text: s.billingCancel };
  if (state.notice === 'queueHeld') return { tone: 'ok', text: s.queueHeld };
  if (state.notice === 'queueRestored') return { tone: 'ok', text: s.queueRestored };
  if (state.notice === 'claimed') return { tone: 'ok', text: s.savedForAccount };
  if (state.notice === 'account:deleted') return { tone: 'ok', text: s.accountDeleted };
  if (state.notice === 'claimedFresh') return { tone: 'ok', text: s.claimedFresh };
  if (state.notice === 'password:ok') return { tone: 'ok', text: s.savedForAccount };
  if (state.notice === 'push:on') return { tone: 'ok', text: s.pushOn };
  if (state.notice === 'push:off') return { tone: 'ok', text: s.pushOff };
  if (state.notice === 'push:test') return { tone: 'ok', text: s.pushTestSent };
  if (state.notice === 'verify:ok') return { tone: 'ok', text: s.verifyOk };
  if (state.notice === 'verify:sent') return { tone: 'ok', text: s.verifySent };
  if (state.notice === 'reset:sent') return { tone: 'ok', text: s.resetSent };
  if (state.notice === 'reset:done') return { tone: 'ok', text: s.resetDone };
  if (state.notice === 'verify:notSent') return { tone: 'error', text: s.verifyNotSent };
  if (state.notice === 'vehicle:saved') return { tone: 'ok', text: s.vehicleSaved };
  if (state.notice === 'rates:ok') return { tone: 'ok', text: s.ratesUpdated };
  if (state.notice?.startsWith('rates:backfilled:')) {
    return { tone: 'ok', text: s.ratesBackfilled(Number(state.notice.split(':')[2])) };
  }
  if (state.notice === 'rates:backfillNone') return { tone: 'ok', text: s.ratesBackfillNone };
  if (state.notice?.startsWith('prices:backfilled:')) {
    return { tone: 'ok', text: s.pricesBackfilled(Number(state.notice.split(':')[2])) };
  }
  if (state.notice === 'prices:backfillNone') return { tone: 'ok', text: s.pricesBackfillNone };
  if (state.notice === 'ai:nothing') return { tone: 'ok', text: s.aiNothing };
  if (state.notice === 'ai:allset') return { tone: 'ok', text: s.aiAllSet };
  if (state.notice?.startsWith('ai:sorted:')) {
    const [applied, asked, searches] = state.notice.split(':').slice(2).map(Number);
    return { tone: 'ok', text: s.aiSorted(applied, asked, searches || 0) };
  }
  if (state.notice?.startsWith('prices:backfillMissed:')) {
    return {
      tone: 'error',
      text: s.pricesBackfillMissed(state.notice.slice('prices:backfillMissed:'.length))
    };
  }
  if (state.notice?.startsWith('rates:backfillMissed:')) {
    return {
      tone: 'error',
      text: s.ratesBackfillMissed(state.notice.slice('rates:backfillMissed:'.length))
    };
  }
  if (state.notice?.startsWith('recurring:posted:')) {
    return { tone: 'ok', text: s.recurringPosted(Number(state.notice.split(':')[2])) };
  }
  if (state.notice === 'recurring:none') return { tone: 'ok', text: s.recurringNone };
  if (state.notice?.startsWith('admin:saved:')) {
    return { tone: 'ok', text: s.adminSaved(state.notice.slice('admin:saved:'.length)) };
  }
  if (state.notice?.startsWith('import:done:')) {
    const [imported, skipped, duplicates] = state.notice.split(':').slice(2).map(Number);
    return { tone: 'ok', text: s.importDone(imported, skipped, duplicates || 0) };
  }
  if (state.notice?.startsWith('export:done:')) {
    return { tone: 'ok', text: s.exportDone(Number(state.notice.split(':')[2])) };
  }
  if (state.notice?.startsWith('prices:noRate:')) {
    /*
     * Not a partial success: nothing was written for these, and saying
     * "updated" while that was true is how a wrong figure survives a refresh.
     */
    return { tone: 'error', text: s.pricesNoRate(state.notice.slice('prices:noRate:'.length)) };
  }
  if (state.notice?.startsWith('prices:converted:')) {
    return { tone: 'ok', text: s.pricesConverted(Number(state.notice.split(':')[2])) };
  }
  if (state.notice?.startsWith('rates:auto:')) {
    return { tone: 'ok', text: s.ratesAuto(state.notice.slice('rates:auto:'.length)) };
  }
  if (state.notice?.startsWith('rates:partial:')) {
    return { tone: 'ok', text: s.ratesPartial(state.notice.slice('rates:partial:'.length)) };
  }
  return null;
}
